import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import {
  BRAND, Icon, Button, Field, Input, Chip, Stepper, Frame, IconChip,
} from '../../../components/onboarding/primitives';
import { onboardingAPI } from '../../../services/api';
import { SAUDI_CITIES } from '../../../data/saudi-cities';

const STEP_LABELS = ['الحساب', 'المختبر', 'الفروع', 'الباقة', 'الأجهزة', 'الدفع'];

const emptyBranch = () => ({
  id: Date.now() + Math.floor(Math.random() * 1000),
  name: '',
  city: '',
  address: '',
  supervisor_name: '',
  supervisor_mobile: '',
  supervisor_email: '',
});

/**
 * Props contract: { state, setState, goToStep, goBack }
 */
export default function Step3Branches({ state, setState, goToStep, goBack }) {
  const branches = (state.s3?.branches && state.s3.branches.length)
    ? state.s3.branches
    : [{ ...emptyBranch(), id: 1 }];

  const setBranches = (next) => setState({ ...state, s3: { ...state.s3, branches: next } });

  const addBranch = () => setBranches([...branches, emptyBranch()]);
  const removeBranch = (id) => setBranches(branches.filter((b) => b.id !== id));
  const updateBranch = (id, k, v) =>
    setBranches(branches.map((b) => (b.id === id ? { ...b, [k]: v } : b)));

  const main = branches[0];
  const mobileOk = !main.supervisor_mobile || /^05\d{8}$/.test(main.supervisor_mobile);
  const canNext = !!(
    main.name
    && main.city
    && main.supervisor_name
    && main.supervisor_mobile
    && mobileOk
  );

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!canNext || submitting) return;

    // Validate all branches' phone fields before hitting API.
    const badIdx = branches.findIndex(
      (b) => b.supervisor_mobile && !/^05\d{8}$/.test(b.supervisor_mobile),
    );
    if (badIdx !== -1) {
      toast.error(`تحقق من رقم جوال المشرف في الفرع رقم ${badIdx + 1}`);
      return;
    }

    setSubmitting(true);
    try {
      await onboardingAPI.createBranches({
        branches: branches.map((b, i) => ({
          branch_name: b.name,
          city: b.city,
          address: b.address || undefined,
          supervisor_name: b.supervisor_name,
          supervisor_mobile: b.supervisor_mobile,
          supervisor_email: b.supervisor_email || undefined,
          is_main: i === 0,
        })),
      });
      toast.success('تم حفظ الفروع');
      goToStep(4);
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.error
        || 'تعذّر حفظ الفروع. حاول مرة أخرى.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onBack = () => { if (goBack) goBack(); else goToStep(2); };

  return (
    <Frame
      maxWidth={820}
      stepper={<Stepper steps={STEP_LABELS} current={2} />}
      footer={
        <>
          <Button variant="outline" onClick={onBack} iconLeft={<Icon.Arrow dir="right" />}>السابق</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canNext || submitting}
            icon={submitting ? <Icon.Clock size={16} /> : <Icon.Arrow />}
          >
            {submitting ? 'جارٍ الحفظ...' : 'التالي'}
          </Button>
        </>
      }
    >
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.5 }}>
          فروع المختبر
        </h1>
        <p style={{ fontSize: 14.5, color: BRAND.muted, margin: 0 }}>
          ابدأ بالفرع الرئيسي — يمكنك إضافة فروع مدفوعة لاحقاً.
        </p>
        <div style={{ marginTop: 12, fontSize: 13, color: BRAND.muted }}>
          إجمالي الفروع:{' '}
          <span style={{ color: BRAND.text, fontWeight: 600 }}>{branches.length}</span>
        </div>
      </div>

      {branches.map((b, i) => (
        <BranchCard
          key={b.id}
          branch={b}
          index={i}
          isMain={i === 0}
          onRemove={() => removeBranch(b.id)}
          onUpdate={(k, v) => updateBranch(b.id, k, v)}
        />
      ))}

      <button
        type="button"
        onClick={addBranch}
        style={{
          width: '100%', padding: '18px', background: '#fff',
          border: `1.5px dashed ${BRAND.border}`, borderRadius: 16,
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8,
          color: BRAND.greenDark, fontWeight: 600, fontSize: 14.5,
          fontFamily: 'inherit', transition: 'all .15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = BRAND.green;
          e.currentTarget.style.background = BRAND.mint;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = BRAND.border;
          e.currentTarget.style.background = '#fff';
        }}
      >
        <Icon.Plus /> إضافة فرع آخر
      </button>
      <p style={{ fontSize: 12, color: BRAND.muted, textAlign: 'center', margin: '12px 0 0' }}>
        الفرع الإضافي يُحتسب في الفاتورة حسب الباقة المختارة
      </p>
    </Frame>
  );
}

// ─── BranchCard ─────────────────────────────────────────
function BranchCard({ branch, index, isMain, onRemove, onUpdate }) {
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const cityRef = useRef();
  const mobileOk = !branch.supervisor_mobile || /^05\d{8}$/.test(branch.supervisor_mobile);

  useEffect(() => {
    const handler = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) setCityOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div style={{
      background: '#fff', border: `1px solid ${BRAND.border}`,
      borderRadius: 20, padding: 28, marginBottom: 16,
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <IconChip size={36}><Icon.Branch /></IconChip>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {isMain ? 'الفرع الرئيسي' : `فرع إضافي ${index}`}
            </div>
            <div style={{ fontSize: 12.5, color: BRAND.muted, marginTop: 2 }}>
              {isMain ? 'مضمّن في الباقة' : 'يُضاف كرسم سنوي'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isMain
            ? <Chip tone="mint">◉ الرئيسي</Chip>
            : <Chip tone="neutral">سيُسعّر في الخطوة القادمة</Chip>}
          {!isMain && (
            <button
              type="button"
              onClick={onRemove}
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'transparent', border: `1px solid ${BRAND.border}`,
                cursor: 'pointer', color: BRAND.muted,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon.X size={14} />
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 18px' }}>
        <Field label="اسم الفرع" required>
          <Input
            value={branch.name}
            onChange={(v) => onUpdate('name', v)}
            placeholder={isMain ? 'الفرع الرئيسي - الرياض' : 'فرع جدة'}
            autoFocus={!isMain && !branch.name}
          />
        </Field>
        {/* City picker — see Step2LabInfo for the rationale: the dropdown
            sits OUTSIDE the <Field> wrapper so option clicks aren't
            re-routed by the surrounding <label> back into the input
            (which would re-open the menu via its onClick handler). */}
        <div ref={cityRef} style={{ position: 'relative' }}>
          <Field label="المدينة" required>
            <div onMouseDown={() => setCityOpen(true)} style={{ cursor: 'pointer' }}>
              <Input
                value={cityOpen ? citySearch : (branch.city || '')}
                onChange={(v) => { setCitySearch(v); setCityOpen(true); }}
                placeholder={branch.city ? branch.city : 'اختر المدينة'}
                rightSlot={
                  <svg
                    width="12" height="8" viewBox="0 0 12 8" fill="none"
                    style={{ transition: 'transform 0.18s ease', transform: cityOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  >
                    <path d="M1 1l5 5 5-5" stroke={BRAND.muted} strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                }
              />
            </div>
          </Field>
          {cityOpen && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0,
              background: '#fff', border: `1px solid ${BRAND.border}`,
              borderRadius: 12, marginTop: 4, maxHeight: 220, overflowY: 'auto',
              zIndex: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            }}>
              {(() => {
                const filtered = SAUDI_CITIES.filter((c) => !citySearch || c.includes(citySearch));
                if (!filtered.length) {
                  return (
                    <div style={{ padding: '14px', fontSize: 13, color: BRAND.muted, textAlign: 'center' }}>
                      لا توجد نتائج مطابقة
                    </div>
                  );
                }
                return filtered.map((c) => {
                  const selected = c === branch.city;
                  return (
                    <div
                      key={c}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onUpdate('city', c);
                        setCityOpen(false);
                        setCitySearch('');
                      }}
                      style={{
                        padding: '10px 14px', fontSize: 14, cursor: 'pointer',
                        borderBottom: `1px solid ${BRAND.surfaceAlt}`,
                        background: selected ? BRAND.mint : 'transparent',
                        fontWeight: selected ? 600 : 400,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      }}
                      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = BRAND.mint; }}
                      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span>{c}</span>
                      {selected && (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M2 7.5l3 3 7-7" stroke={BRAND.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>

      <Field label="العنوان التفصيلي">
        <Input
          value={branch.address}
          onChange={(v) => onUpdate('address', v)}
          placeholder="شارع، حي، مبنى"
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 18px' }}>
        <Field label="اسم المشرف" required>
          <Input
            value={branch.supervisor_name}
            onChange={(v) => onUpdate('supervisor_name', v)}
          />
        </Field>
        <Field
          label="جوال المشرف"
          required
          error={branch.supervisor_mobile && !mobileOk ? 'تنسيق الجوال 05XXXXXXXX' : null}
        >
          <Input
            value={branch.supervisor_mobile}
            onChange={(v) => onUpdate('supervisor_mobile', v.replace(/\D/g, '').slice(0, 10))}
            placeholder="05XXXXXXXX"
            ltr
            maxLength={10}
          />
        </Field>
        <Field label="إيميل المشرف" optional>
          <Input
            value={branch.supervisor_email}
            onChange={(v) => onUpdate('supervisor_email', v)}
            ltr
          />
        </Field>
      </div>
    </div>
  );
}
